import React, { useState } from 'react';
import { useAuth } from '../../../AuthContext';
import { apiUrl } from '../../../api/client';
import { isErrorResponse } from '../../../types/ErrorResponse';
import { PasswordInput } from '../../../shared/PasswordInput';
import { ApiRoutes } from '../../../types/ApiRoutes';

type SignupFormProps = {
  loadingStatus: boolean;
  signupEnabled: boolean;
};

export const SignupForm: React.FC<SignupFormProps> = ({ loadingStatus, signupEnabled }) => {
  const [email, setEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [signupConfirm, setSignupConfirm] = useState('');
  const [signupLicenseAccepted, setSignupLicenseAccepted] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showLicenseModal, setShowLicenseModal] = useState(false);
  const [licenseHtml, setLicenseHtml] = useState<string>('');
  const [loadingLicense, setLoadingLicense] = useState(false);
  const { signup } = useAuth();

  const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  const isStrongPassword = (value: string) =>
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[^A-Za-z0-9]).{8,}$/.test(value);

  const fetchLicenseAgreement = async () => {
    if (licenseHtml) {
      setShowLicenseModal(true);
      return;
    }
    setLoadingLicense(true);
    try {
      const response = await fetch(apiUrl(ApiRoutes.LicenseAgreement));
      if (response.ok) {
        const html = await response.text();
        setLicenseHtml(html);
        setShowLicenseModal(true);
      } else {
        setError(
          `Failed to load license agreement (Status: ${response.status}). Please try again.`
        );
      }
    } catch {
      setError('Failed to load license agreement. Please check your connection and try again.');
    } finally {
      setLoadingLicense(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    if (!signupEnabled) {
      setError(
        'Thank you for your interest in Walk It With Me. New signups are not currently allowed. Please check again later for availability.'
      );
      return;
    }
    if (!signupLicenseAccepted) {
      setError('You must accept the license agreement to sign up.');
      return;
    }
    const emailTrimmed = email.trim();
    if (!isValidEmail(emailTrimmed)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (signupPassword !== signupConfirm) {
      setError('Passwords do not match.');
      return;
    }
    if (!isStrongPassword(signupPassword)) {
      setError(
        'Password must be at least 8 characters and include an uppercase letter, lowercase letter, number, and symbol.'
      );
      return;
    }
    try {
      setSubmitting(true);
      await signup(emailTrimmed, signupPassword);
      setSuccess(true);
      // Clear the form
      setEmail('');
      setSignupPassword('');
      setSignupConfirm('');
      setSignupLicenseAccepted(false);
    } catch (err: unknown) {
      if (isErrorResponse(err)) {
        setError(err.message);
      } else {
        setError('Signup failed');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="login-form">
        <h2 className="h4 mb-3">Sign Up</h2>
        {loadingStatus ? (
          <div className="text-muted d-flex align-items-center" aria-live="polite">
            <span
              className="spinner-border spinner-border-sm me-2 text-primary"
              role="status"
              aria-hidden="true"
            ></span>
            Checking signup availability
          </div>
        ) : !signupEnabled ? (
          <div className="alert alert-info" role="alert">
            Thank you for your interest in Walk It With Me. New signups are not currently allowed.
            Please check again later for availability.
          </div>
        ) : success ? (
          <div className="alert alert-success" role="alert">
            Account created successfully! Please check your email to confirm your account and
            logging in.
          </div>
        ) : (
          <>
            <div className="mb-3">
              <label htmlFor="signupEmail" className="form-label">
                Email
              </label>
              <input
                id="signupEmail"
                type="email"
                className="form-control"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <PasswordInput
              id="signupPassword"
              label="Password"
              value={signupPassword}
              onChange={(e) => setSignupPassword(e.target.value)}
              required
              minLength={8}
              pattern="(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[^A-Za-z0-9]).{8,}"
              title="At least 8 characters with uppercase, lowercase, number, and symbol"
              autoComplete="new-password"
            />
            <PasswordInput
              id="signupConfirm"
              label="Confirm Password"
              value={signupConfirm}
              onChange={(e) => setSignupConfirm(e.target.value)}
              required
              minLength={8}
              pattern="(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[^A-Za-z0-9]).{8,}"
              title="Must match and meet password complexity"
              autoComplete="new-password"
            />
            <div className="mb-3 form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="signupLicenseCheckbox"
                checked={signupLicenseAccepted}
                onChange={(e) => setSignupLicenseAccepted(e.target.checked)}
                required
              />
              <label className="form-check-label" htmlFor="signupLicenseCheckbox">
                I have read and agree to the Walk It With Me{' '}
                <button
                  type="button"
                  className="btn btn-link p-0 align-baseline"
                  onClick={fetchLicenseAgreement}
                >
                  license agreement
                </button>
              </label>
            </div>
            {success && (
              <div className="alert alert-success py-2" role="alert">
                {success}
              </div>
            )}
            {error && (
              <div className="alert alert-danger py-2" role="alert">
                {error}
              </div>
            )}
            <button
              type="submit"
              className="btn btn-primary w-100"
              disabled={submitting || !signupLicenseAccepted}
            >
              {submitting ? 'Creating account…' : 'Sign Up'}
            </button>
          </>
        )}
      </form>

      {showLicenseModal && (
        <div
          className="modal show d-block modal-overlay-custom"
          tabIndex={-1}
          role="dialog"
          aria-labelledby="licenseModalTitle"
          aria-describedby="licenseModalBody"
        >
          <div className="modal-dialog modal-lg modal-dialog-scrollable" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 id="licenseModalTitle" className="modal-title">
                  License Agreement
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowLicenseModal(false)}
                  aria-label="Close"
                ></button>
              </div>
              <div id="licenseModalBody" className="modal-body">
                {loadingLicense ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2">Loading license agreement...</p>
                  </div>
                ) : (
                  <div dangerouslySetInnerHTML={{ __html: licenseHtml }} />
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowLicenseModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SignupForm;
